<script lang="ts" setup>
import { MergeCompareRow, MergeFieldStatus } from "~/types/profile";

const props = withDefaults(
  defineProps<{
    rows: MergeCompareRow[];
    parentHeading: string;
    childHeading: string;
    childTone?: "removed" | "merged";
    resultHeading?: string;
    emptyText: string;
  }>(),
  { childTone: "removed", resultHeading: undefined },
);

const STATUS_META: Record<
  MergeFieldStatus,
  { label: string; type: "success" | "warning" | "danger" | "info" } | null
> = {
  incoming: { label: "Copied over", type: "success" },
  conflict: { label: "Discarded", type: "warning" },
  dropped: { label: "Lost", type: "danger" },
  same: null,
  unchanged: null,
};

const TONES: Record<string, { heading: string; border: string }> = {
  kept: {
    heading: "text-emerald-600 dark:text-emerald-400",
    border: "border-emerald-200 dark:border-emerald-800",
  },
  removed: {
    heading: "text-rose-600 dark:text-rose-400",
    border: "border-rose-200 dark:border-rose-800",
  },
  merged: {
    heading: "text-sky-600 dark:text-sky-400",
    border: "border-sky-200 dark:border-sky-800",
  },
  result: {
    heading: "text-light-sec-purple dark:text-light-purple-border",
    border: "border-light-purple-border dark:border-light-sec-purple",
  },
};

const columns = computed(() => {
  const built = [
    {
      key: "parent",
      tone: "kept",
      heading: props.parentHeading,
      cells: props.rows.map((row) => ({
        key: row.key,
        label: row.label,
        value: row.parent,
        statusMeta: null as { label: string; type: string } | null,
      })),
    },
    {
      key: "child",
      tone: props.childTone,
      heading: props.childHeading,
      cells: props.rows.map((row) => ({
        key: row.key,
        label: row.label,
        value: row.child,
        statusMeta: row.status ? STATUS_META[row.status] : null,
      })),
    },
  ];

  if (props.resultHeading) {
    built.push({
      key: "result",
      tone: "result",
      heading: props.resultHeading,
      cells: props.rows.map((row) => ({
        key: row.key,
        label: row.label,
        value: row.result ?? null,
        statusMeta: null,
      })),
    });
  }

  return built;
});
</script>

<template>
  <div
    v-if="rows.length"
    class="grid gap-4"
    :style="{
      gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
    }"
  >
    <div
      v-for="column in columns"
      :key="column.key"
      class="rounded-lg border p-3"
      :class="[
        TONES[column.tone].border,
        { 'bg-gray-50 dark:bg-gray-800/40': column.key === 'result' },
      ]"
    >
      <div
        class="mb-2 truncate text-xs font-medium uppercase"
        :class="TONES[column.tone].heading"
      >
        {{ column.heading }}
      </div>
      <div class="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
        <div
          v-for="cell in column.cells"
          :key="`${column.key}-${cell.key}`"
          class="flex items-start justify-between gap-3 py-1.5 text-sm"
        >
          <span class="shrink-0 text-gray-500">{{ cell.label }}</span>
          <div class="flex flex-wrap items-center justify-end gap-2">
            <span v-if="cell.value" class="break-words text-right">
              {{ cell.value }}
            </span>
            <span v-else class="text-gray-400">—</span>
            <el-tag
              v-if="cell.statusMeta"
              :type="cell.statusMeta.type"
              size="small"
              disable-transitions
            >
              {{ cell.statusMeta.label }}
            </el-tag>
          </div>
        </div>
      </div>
    </div>
  </div>

  <el-empty v-else :image-size="50" :description="emptyText" />
</template>
